import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../api/client';
import StarRating from '../../components/StarRating';
import { useToast } from '../../context/ToastContext';

const CATEGORIES = ['General', 'Maintenance', 'Cleanliness', 'Security', 'Service', 'Suggestion', 'Complaint'];
const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const STORAGE_KEY = 'tr_visitor_tickets';

function pushLocalTicket(ticketNumber, subject) {
  try {
    const prev = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    prev.unshift({ ticketNumber, subject, at: new Date().toISOString() });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prev.slice(0, 10)));
  } catch {
    /* ignore */
  }
}

export default function TicketForm() {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    visitor_name: '',
    visitor_email: '',
    visitor_phone: '',
    category: 'General',
    priority: 'medium',
    subject: '',
    description: '',
    rating: 5,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/locations/${locationId}`);
        if (!cancelled) setLocation(data.location);
      } catch {
        if (!cancelled) setError('This location is invalid or no longer active.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [locationId]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post('/tickets', {
        location_id: locationId,
        visitor_name: form.visitor_name,
        visitor_email: form.visitor_email || undefined,
        visitor_phone: form.visitor_phone || undefined,
        category: form.category,
        priority: form.priority,
        subject: form.subject,
        description: form.description,
        rating: form.rating,
      });
      const num = data.ticket?.ticket_number;
      pushLocalTicket(num, form.subject);
      toast.success('Ticket submitted');
      navigate('/ticket-success', { state: { ticketNumber: num } });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not submit ticket');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="tr-spinner" />
      </div>
    );
  }

  if (error || !location) {
    return (
      <div style={{ maxWidth: 480, margin: '3rem auto', padding: '1.5rem', textAlign: 'center' }}>
        <div className="tr-card" style={{ padding: '2rem' }}>
          <h1 style={{ color: '#b91c1c' }}>Location unavailable</h1>
          <p style={{ color: 'var(--text-muted)' }}>{error}</p>
          <Link to="/" className="tr-btn tr-btn-primary" style={{ marginTop: '1rem' }}>
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 560, margin: '0 auto', padding: '1.25rem 1rem 3rem' }}>
      <Link to="/" style={{ fontSize: '0.9rem' }}>
        ← Home
      </Link>
      <div className="tr-card" style={{ padding: '1.5rem', marginTop: '1rem' }}>
        <h1 style={{ marginTop: 0, fontSize: '1.35rem' }}>Raise a ticket</h1>
        <p style={{ color: 'var(--text-muted)', marginTop: 0 }}>
          Location: <strong style={{ color: 'var(--primary)' }}>{location.name}</strong>
        </p>

        <form onSubmit={submit}>
          <label className="tr-label">Visitor name *</label>
          <input className="tr-input" value={form.visitor_name} onChange={(e) => setForm({ ...form, visitor_name: e.target.value })} required />

          <label className="tr-label" style={{ marginTop: '0.85rem' }}>
            Email
          </label>
          <input className="tr-input" type="email" value={form.visitor_email} onChange={(e) => setForm({ ...form, visitor_email: e.target.value })} />

          <label className="tr-label" style={{ marginTop: '0.85rem' }}>
            Phone
          </label>
          <input className="tr-input" value={form.visitor_phone} onChange={(e) => setForm({ ...form, visitor_phone: e.target.value })} />

          <label className="tr-label" style={{ marginTop: '0.85rem' }}>
            Category
          </label>
          <select className="tr-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>

          <label className="tr-label" style={{ marginTop: '0.85rem' }}>
            Priority
          </label>
          <select className="tr-input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
            {PRIORITIES.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>

          <label className="tr-label" style={{ marginTop: '0.85rem' }}>
            Subject *
          </label>
          <input className="tr-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required />

          <label className="tr-label" style={{ marginTop: '0.85rem' }}>
            Description *
          </label>
          <textarea className="tr-input" rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required />

          <label className="tr-label" style={{ marginTop: '0.85rem' }}>
            Rating
          </label>
          <StarRating value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} />

          <button type="submit" className="tr-btn tr-btn-primary" style={{ width: '100%', marginTop: '1.25rem' }} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit ticket'}
          </button>
        </form>
      </div>
    </div>
  );
}
